import ErrorWrapper from "components/Others/ErrorWrapper";
import useDiseasesLookupSearch from "hooks/queries/useDiseasesLookupSearch";
import { LookupItem } from "api/types";
import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useQuery } from "react-query";
import { Combobox, MultiCombobox } from "@/components/ui/combobox";
import { inputClass } from "@/lib/formStyles";
import fichePlaceholder from "../../../assets/fiche_placeholder.jpeg";
import { openProtectedFile } from "@/utils/openProtectedFile";
import { updateUser } from "@/redux/users/actions";
import { getStoragePatient } from "utils/storageUtils";
import { getUsersByRole } from "services/users";
import { roles } from "@/constants/roles";
import Alert from "../../Others/Alert";
import BirthDateField from "../../Others/BirthDateField";
import { toast } from "sonner";
import { isValidPhoneIfPresent } from "@/utils/validators";
import {
  BirthDateValue,
  toBirthDateValue,
  validateBirthDateValue,
  withBirthDatePayload,
} from "@/utils/age";

const TabOne = ({ current_patient: curr_patient, uploadLink }: any) => {
  const dispatch = useDispatch();

  const DROPDOWN_PAGE_SIZE = 20;

  const { data: doctorsResult } = useQuery(
    ["fiche-doctors"],
    () => getUsersByRole(roles.ROLE_DOCTOR, "", 0, DROPDOWN_PAGE_SIZE),
    { staleTime: 60000 }
  );
  const { data: insurersResult } = useQuery(
    ["fiche-insurers"],
    () => getUsersByRole(roles.ROLE_INSURER, "", 0, DROPDOWN_PAGE_SIZE),
    { staleTime: 60000 }
  );
  const doctorsList: any[] =
    (doctorsResult as any)?.data?.content || [];
  const insurersList: any[] =
    (insurersResult as any)?.data?.content || [];
  const errors: any = undefined;

  // Lookup async par MultiCombobox — chaque champ a son propre query state
  // pour que la recherche dans "Antécédents familiaux" n'affecte pas la
  // liste de "Antécédents médicaux". Min 2 chars + debounce 250ms géré
  // dans le hook ; pas de fetch tant que `q.length < 2`.
  const [diseaseQueryFamily, setDiseaseQueryFamily] = useState("");
  const [diseaseQueryMedical, setDiseaseQueryMedical] = useState("");
  const familyResult = useDiseasesLookupSearch(diseaseQueryFamily);
  const medicalResult = useDiseasesLookupSearch(diseaseQueryMedical);
  const diseasesFamily: LookupItem[] = familyResult.data ?? [];
  const diseasesMedical: LookupItem[] = medicalResult.data ?? [];
  const loadingFamily =
    diseaseQueryFamily.trim().length >= 2 && familyResult.isFetching;
  const loadingMedical =
    diseaseQueryMedical.trim().length >= 2 && medicalResult.isFetching;

  const storagePatient = getStoragePatient();
  const current_patient = Object.keys(curr_patient).length
    ? curr_patient
    : storagePatient;

  const [update, setUpdate] = useState({
    insurerId: current_patient?.insurer?.id || "",
    medicalFamilyHistoryRequests: [],
    ...current_patient,
  });
  // Date de naissance / âge approximatif : édités à part de `update`, car
  // seul l'un des deux champs part au serveur (cf. `withBirthDatePayload`).
  const [birthDate, setBirthDate] = useState<BirthDateValue>(
    toBirthDateValue(current_patient)
  );

  const handleChangeUpdate = (e: React.ChangeEvent<HTMLInputElement>) =>
    setUpdate((update: any) => ({
      ...update,
      [e.target.name]: e.target.value,
    }));

  // Le lookup endpoint renvoie déjà `{id, label}` (le back concatène déjà
  // code CIM-10 + libellé dans `label`). On conserve `diseasesId` côté
  // payload submit car le DTO MedicalFamilyHistoryRequest l'attend.
  const adaptOptions = (
    data: LookupItem[],
    historyTypeName: string
  ): Array<{
    value: number;
    label: string;
    historyTypeName: string;
    diseasesId: number;
  }> => {
    return data.map((item) => ({
      value: item.id,
      label: item.label,
      historyTypeName,
      diseasesId: item.id,
    }));
  };

  const adaptReturnOptions = (data: any) => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    let temp = data.map((item: any) => ({
      value: item.disease?.id,
      label: item.disease?.name,
      historyTypeName: item?.historyTypeName,
      diseasesId: item.disease?.id,
    }));

    return temp;
  };
  const adaptSelectOptions = (data: any, role: any): Array<{ value: any; label: string }> => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    const temp: Array<{ value: any; label: string }> = [];
    data.forEach((item: any) => {
      if (role === "Doctor" && item?.id) {
        temp.push({
          value: item.id,
          label: (item?.firstName || "") + " " + (item?.lastName || ""),
        });
      } else if (role === "Insurer" && item?.id) {
        temp.push({
          value: item.id,
          label: item?.name || "",
        });
      }
    });

    return temp;
  };

  const handleSubmitUpdate = (e: React.MouseEvent) => {
    e.preventDefault();
    // Validation FORMAT des téléphones — même règle que le backend (vide accepté).
    if (!isValidPhoneIfPresent(update?.phoneNumber)) {
      toast.error(
        `Téléphone principal invalide : « ${update?.phoneNumber} ». Il doit commencer par 01 puis 4, 5, 6 ou 9, et 10 chiffres au total.`
      );
      return;
    }
    if (!isValidPhoneIfPresent(update?.emergencyContactPhone)) {
      toast.error(
        `Téléphone d'urgence invalide : « ${update?.emergencyContactPhone} ». Il doit commencer par 01 puis 4, 5, 6 ou 9, et 10 chiffres au total.`
      );
      return;
    }
    const birthDateError = validateBirthDateValue(birthDate);
    if (birthDateError) {
      toast.error(birthDateError);
      return;
    }
    dispatch(
      updateUser(
        withBirthDatePayload(
          {
            ...update,
            insurerId: insurer?.value,
            doctorId: doctor?.value,
            medicalFamilyHistoryRequests: [
              ...selectedDiseaseFamily,
              ...selectedDiseaseMedical,
            ],
            consent: "", // uploadLink
          },
          birthDate
        )
      )
    );
  };

  const uniqueItems = (data: any) => {
    const unique: any = [
      ...new Map(data.map((m: any) => [m?.diseasesId, m])).values(),
    ];
    return unique;
  };

  const [selectedDiseaseMedical, setSelectedDiseaseMedical] = useState(
    uniqueItems([
      ...adaptReturnOptions(current_patient?.medicalFamilyHistories || []).filter(
        (item: any) => item.historyTypeName === "MEDICAL_HISTORY"
      ),
    ])
  );
  const [selectedDiseaseFamily, setSelectedDiseaseFamily] = useState(
    uniqueItems([
      ...adaptReturnOptions(current_patient?.medicalFamilyHistories || []).filter(
        (item: any) => item.historyTypeName === "FAMILY_HISTORY"
      ),
    ])
  );

  const [insurer, setInsurer] = useState({
    value: current_patient?.insurer?.id || null,
    label: current_patient?.insurer?.name || "",
  });
  const [doctor, setDoctor] = useState({
    value: current_patient?.doctor?.id || null,
    label:
      (current_patient?.doctor?.firstName || "") +
      " " +
      (current_patient?.doctor?.lastName || ""),
  });
  const [sex, setSex] = useState({
    value: current_patient?.sex,
    label:
      current_patient?.sex === "F"
        ? "Mme"
        : current_patient?.sex === "M"
        ? "Mr"
        : "",
  });
  
  // Update form state when current_patient changes
  useEffect(() => {
    // Get the latest patient from Redux or localStorage
    const latestStoragePatient = getStoragePatient();
    const patient = Object.keys(curr_patient).length ? curr_patient : latestStoragePatient;
    
    if (patient && patient.id) {
      setUpdate({
        insurerId: patient?.insurer?.id || "",
        medicalFamilyHistoryRequests: [],
        ...patient,
      });

      setBirthDate(toBirthDateValue(patient));

      setInsurer({
        value: patient?.insurer?.id || null,
        label: patient?.insurer?.name || "",
      });
      
      setDoctor({
        value: patient?.doctor?.id || null,
        label:
          (patient?.doctor?.firstName || "") +
          " " +
          (patient?.doctor?.lastName || ""),
      });
      
      setSex({
        value: patient?.sex,
        label:
          patient?.sex === "F"
            ? "Mme"
            : patient?.sex === "M"
            ? "Mr"
            : "",
      });
      
      setSelectedDiseaseMedical(
        uniqueItems([
          ...adaptReturnOptions(patient?.medicalFamilyHistories || []).filter(
            (item: any) => item.historyTypeName === "MEDICAL_HISTORY"
          ),
        ])
      );
      
      setSelectedDiseaseFamily(
        uniqueItems([
          ...adaptReturnOptions(patient?.medicalFamilyHistories || []).filter(
            (item: any) => item.historyTypeName === "FAMILY_HISTORY"
          ),
        ])
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curr_patient?.id]); // Re-run when patient ID changes in Redux

  // Les listes sont déjà filtrées par rôle côté back — on ne refait pas le
  // filtre côté front pour éviter un round-trip mental.
  const insurerOptions = adaptSelectOptions(insurersList, "Insurer");
  const doctorsOptions = adaptSelectOptions(doctorsList, "Doctor");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [imageError, setImageError] = useState("");

  return (
    <div data-tab="fiche">
      <form className="space-y-6">
        {/* Section header — eyebrow à gauche, identifiant compact à droite */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Informations patient
          </span>
          <span className="flex-1 h-px bg-slate-100 min-w-[1rem]" />
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-mono uppercase text-slate-600">
            <i className="bx bx-id-card text-sm text-slate-400" />
            {current_patient?.registrationNumber || "—"}
          </span>
        </div>
        <ErrorWrapper errors={errors}/>
        {/* Titre · Nom · Prénom — grille 12 cols (2/5/5) avec items-end pour
            aligner les champs malgré les hauteurs de label différentes. */}
        <div className="grid grid-cols-2 sm:grid-cols-12 gap-4 items-end">
          <label className="block col-span-2 sm:col-span-2">
            <span className="block text-sm font-medium text-slate-500">
              Titre
            </span>
            {/* mt-1 sur le wrapper : Combobox n'a pas son `mt-1` baked-in
                comme inputClass — sans cette respiration manuelle, le
                trigger sit 4 px au-dessus des <input> à côté. */}
            <div className="mt-1">
              <Combobox
                  options={[
                    {value: "M", label: "Mr"},
                    {value: "F", label: "Mme"},
                  ]}
                  value={sex?.value}
                  onChange={(opt) => setSex(opt as any)}
                  className="w-full"
              />
            </div>
          </label>
          <label className="block col-span-2 sm:col-span-5">
            <span className="block text-sm font-medium text-slate-500">
              Nom
            </span>
            <input
                type="text"
                name="lastName"
                onChange={handleChangeUpdate}
                value={update?.lastName}
                className={inputClass}
            />
          </label>
          <label className="block col-span-2 sm:col-span-5">
            <span className="block text-sm font-medium text-slate-500">
              Prénom
            </span>
            <input
                type="text"
                name="firstName"
                onChange={handleChangeUpdate}
                value={update?.firstName}
                className={inputClass}
            />
          </label>
        </div>
        <BirthDateField
            value={birthDate}
            onChange={setBirthDate}
            estimated={Boolean(current_patient?.dateOfBirthEstimated)}
            labelClassName="block text-sm font-medium text-slate-500"
        />
          <div className="space-y-5 sm:space-y-0 sm:flex sm:space-x-4">
            <label className="block w-auto sm:grow sm:basis-full">
              <span className="block text-sm font-medium text-slate-500">
                Email
              </span>
              <input
                  type="text"
                  name="email"
                  readOnly
                  value={update?.email}
                  className="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
              />
            </label>
            <label className="block w-auto sm:grow sm:basis-full">
              <span className="block text-sm font-medium text-slate-500">
                Profession
              </span>
              <input
                  type="text"
                  name="occupation"
                  onChange={handleChangeUpdate}
                  value={update?.occupation}
                  className="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
              />
            </label>
          </div>
          <div className="space-y-5 sm:space-y-0 sm:flex sm:space-x-4">
            <label className="block w-auto sm:grow sm:basis-full">
              <span className="block text-sm font-medium text-slate-500">
                Contact
              </span>
              <input
                  type="text"
                  name="phoneNumber"
                  onChange={handleChangeUpdate}
                  value={update?.phoneNumber}
                  className="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
              />
            </label>
            <label className="block w-auto sm:grow sm:basis-full">
              <span className="block text-sm font-medium text-slate-500">
                Adresse
              </span>
              <input
                  type="text"
                  name="address"
                  onChange={handleChangeUpdate}
                  value={update?.address}
                  className="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
              />
            </label>
          </div>
          <div className="space-y-5 sm:space-y-0 sm:flex sm:space-x-4">
            <label className="block w-auto sm:grow sm:basis-full">
              <span className="block text-sm font-medium text-slate-500">
                Personne à contacter (urgence)
              </span>
              <input
                  type="text"
                  name="emergencyContactName"
                  onChange={handleChangeUpdate}
                  value={update?.emergencyContactName || ""}
                  className="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
              />
            </label>
            <label className="block w-auto sm:grow sm:basis-full">
              <span className="block text-sm font-medium text-slate-500">
                Téléphone d'urgence
              </span>
              <input
                  type="tel"
                  name="emergencyContactPhone"
                  onChange={handleChangeUpdate}
                  value={update?.emergencyContactPhone || ""}
                  placeholder="01XXXXXXXX"
                  className="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
              />
            </label>
          </div>
          <div className="space-y-5 sm:space-y-0 sm:flex sm:space-x-4">
            <label className="block w-auto sm:grow sm:basis-full">
              <span className="block text-sm font-medium text-slate-500">
                Numéro NPI
              </span>
              <input
                  type="text"
                  name="npiNumber"
                  onChange={handleChangeUpdate}
                  value={update?.npiNumber || ""}
                  className="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
              />
            </label>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Suivi médical
            </span>
            <span className="flex-1 h-px bg-slate-100" />
          </div>
          <div className="space-y-5 sm:space-y-0 sm:flex sm:space-x-4">
            <label className="block w-auto sm:grow sm:basis-full">
              <span className="block text-sm font-medium text-slate-500">
                Assurée par/Entreprise
              </span>
              <Combobox
                  options={insurerOptions}
                  value={insurer?.value}
                  onChange={(opt) => setInsurer(opt as any)}
                  placeholder="Choix de l'assureur"
                  searchPlaceholder="Rechercher un assureur..."
                  className="w-full"
              />
            </label>
            <label className="block w-auto sm:grow sm:basis-full">
              <span className="block text-sm font-medium text-slate-500">
                Médécin traitant
              </span>
              {/* <input type="text" className="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10" /> */}
              <Combobox
                  options={doctorsOptions}
                  value={doctor?.value}
                  onChange={(opt) => setDoctor(opt as any)}
                  placeholder="Choix du médecin"
                  searchPlaceholder="Rechercher un médecin..."
                  className="w-full"
              />
            </label>
          </div>
          {insurer.value !== null && insurer.value !== undefined && (
              <div className="space-y-4 sm:space-y-0 sm:flex sm:space-x-4">
                <label className="block sm:grow sm:basis-full">
                <span className="block text-sm font-medium text-slate-500">
                  Pourcentage sur examen du patient
                </span>
                  <input
                      name="examPercent"
                      value={update?.examPercent}
                      onChange={handleChangeUpdate}
                      type="number"
                      min={0}
                      className="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
                  />
                </label>
                <label className="block sm:grow sm:basis-full">
                <span className="block text-sm font-medium text-slate-500">
                  Pourcentage sur consultation du patient
                </span>
                  <input
                      type="number"
                      min={0}
                      name="consultationPercent"
                      value={update?.consultationPercent}
                      onChange={handleChangeUpdate}
                      className="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
                  />
                </label>
              </div>
          )}
          {/* <!--<div className="space-y-5 sm:space-y-0 sm:flex sm:space-x-4">
                            <label className="block w-auto sm:grow sm:basis-full">

                                <span className="block text-sm font-medium text-slate-500">Antécédents familiaux</span>
                                <select className="w-full text-slate-500 mt-1 py-2 border-none ring-1 ring-gray-200 focus:ring-gray-200 placeholder-slate-400 rounded-md" multiple="multiple">
                                    <option value="Grippe">Grippe</option>

                                    <option value="Cancer">Cancer</option>

                                </select>

                            </label>
                            <label className="block w-auto sm:grow sm:basis-full">
                                <span className="block text-sm font-medium text-slate-500">Antécédents médicaux</span>

                                <select className="w-full text-slate-500 mt-1 py-2 border-none ring-1 ring-gray-200 focus:ring-gray-200 placeholder-slate-400 rounded-md">

                                    <option value="Cancer">Cancer</option>
                                    <option value="Grippe">Grippe</option>

                                </select>

                            </label>
                        </div>--> */}
          <label className="block">
            <span className="block text-sm font-medium text-slate-500">
              Antécédents familiaux
            </span>
            <MultiCombobox
                value={selectedDiseaseFamily as any}
                onChange={(opts) => setSelectedDiseaseFamily(opts as any)}
                options={adaptOptions(diseasesFamily, "FAMILY_HISTORY")}
                placeholder="Sélectionner des antécédents familiaux..."
                searchPlaceholder="Rechercher une maladie..."
                onSearchChange={setDiseaseQueryFamily}
                loading={loadingFamily}
                emptyText={
                  diseaseQueryFamily.trim().length < 2
                    ? "Tapez au moins 2 caractères"
                    : "Aucun résultat"
                }
                className="w-full"
            />
            {/* <select className="antecedent-familiaux w-full text-slate-500 mt-1 py-2 border-none ring-1 ring-gray-200 focus:ring-gray-200 placeholder-slate-400 rounded-md" multiple="multiple">
                          <option></option>
                          {diseases.map((disease, idx) => (<option key={idx}>{disease?.name}</option>))}
                          <option value="Grippe">Grippe</option>
                          <option value="Cancer">Cancer</option>
                        </select> */}
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-slate-500">
              Antécédents médicaux
            </span>
            <MultiCombobox
                value={selectedDiseaseMedical as any}
                onChange={(opts) => setSelectedDiseaseMedical(opts as any)}
                options={adaptOptions(diseasesMedical, "MEDICAL_HISTORY")}
                placeholder="Sélectionner des antécédents médicaux..."
                searchPlaceholder="Rechercher une maladie..."
                onSearchChange={setDiseaseQueryMedical}
                loading={loadingMedical}
                emptyText={
                  diseaseQueryMedical.trim().length < 2
                    ? "Tapez au moins 2 caractères"
                    : "Aucun résultat"
                }
                className="w-full"
            />
            {/* <select className="antecedent-medicaux w-full text-slate-500 mt-1 py-2 border-none ring-1 ring-gray-200 focus:ring-gray-200 placeholder-slate-400 rounded-md" multiple="multiple">
                          {drugs.map((drug, idx) => <option key={idx} value={drug?.id}>{drug?.name}</option>)}
                          <option value="Grippe">Grippe</option>
                          <option value="palu">palu</option>
                        </select> */}
          </label>
          <div className="space-y-3">
            <span className="block text-sm font-medium text-slate-500">
              Fiche de consentement
            </span>
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 md:space-x-9">
              <div className="shrink-0">
                <img
                    className="h-20 w-20 sm:h-24 sm:w-24 object-cover rounded-sm ring-2 ring-offset-4 ring-gray-200 hover:ring-green-200 transition-all ease-in-out duration-300"
                    src={fichePlaceholder}
                    alt="Fiche consentement"
                />
              </div>
              <label className="block">
                <span className="sr-only">Consentement</span>
                {imageError.length > 0 && <Alert message={imageError}/>}
                {/* {JSON.stringify(uploadLink)} */}
                <input
                    type="file"
                    onChange={() => {
                      // TODO Brain : input fiche consentement — domaine
                      // `uploads` supprimé en Phase 2.G. À recâbler une
                      // fois qu'un endpoint d'upload back existera.
                    }}
                    className="block w-full text-xs sm:text-sm text-slate-500 file:text-xs sm:file:text-sm file:font-semibold file:py-2 file:px-4 file:bg-green-50 file:text-green-700 file:rounded-full file:border-0 file:mr-4 hover:file:bg-green-100"
                />
              </label>
            </div>
          </div>
          <label className="relative block">
            <span className="block text-sm font-medium text-slate-600">
              Autres à préciser
            </span>
            <textarea
                className="w-full mt-1 h-32 resize-none rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"></textarea>
          </label>
          <div className="flex flex-wrap items-center gap-3 pt-4">
            <button
                type="submit"
                onClick={handleSubmitUpdate}
                className="group/btn relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
            >
              <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full" />
              <i className="bx bx-save text-lg" />
              Mettre à jour
            </button>
            <button
                type="button"
                onClick={() => {
                  if (!current_patient?.id) return;
                  openProtectedFile(
                    `/users/patient-card-pdf/${current_patient.id}`,
                    { loadingMessage: "Téléchargement de la fiche…" }
                  );
                }}
                disabled={!current_patient?.id}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-emerald-700 hover:border-emerald-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <i className="bx bx-printer text-lg" />
              Imprimer
            </button>
          </div>
      </form>
    </div>
  );
};

const mapStateToProps = ({usersReducer}: any) => ({
  current_patient: usersReducer.current_patient,
});

export default connect(mapStateToProps)(TabOne);
